import React, { useRef, useEffect, useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { X, Home, MessageSquare, History, Plus, User, Users, Settings, LayoutDashboard, Image, FileText, Sun, Moon, ChevronUp, ChevronDown, BarChart3, Database, Folder, FolderOpen, Pin, Mail, PanelLeftClose, PanelLeftOpen, Calculator, Bell, Wrench, MessageCircle, BookOpen, StickyNote, CheckSquare, GitBranch, ClipboardCheck, Trophy, Zap, MonitorPlay, Newspaper, HandPlatter, Inbox, HardHat, GraduationCap } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiGetJson as apiGet } from '../../utils/api';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useProjects } from '../../context/ProjectContext';
import { useScreensaver } from '../../context/ScreensaverContext';
import SidebarFeatureItem from './SidebarFeatureItem';
import SidebarInfoTip from './SidebarInfoTip';

function DashboardSidebar({ isOpen, setIsOpen, collapsed, setCollapsed, unreadCount = 0, teamChatUnreadCount = 0, taskCount = 0, onOpenDrawer }) {
    const { theme, toggleTheme } = useTheme();
    const { currentUser, logout, canAccess, isLocked } = useAuth();
    const { projects, currentProject, selectProject } = useProjects();
    const { manualTrigger: triggerScreensaver } = useScreensaver();
    const navigate = useNavigate();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [projectsExpanded, setProjectsExpanded] = useState(false);
    const [toolsExpanded, setToolsExpanded] = useState(false);
    const [analyticsExpanded, setAnalyticsExpanded] = useState(false);
    const [communicationExpanded, setCommunicationExpanded] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownRef]);

    // News unread badge — polls every 2 minutes
    const { data: newsUnread } = useQuery({
        queryKey: ['news-unread-count'],
        queryFn: async () => {
            try { return await apiGet('/news/unread_count'); } catch { return { unread: 0 }; }
        },
        enabled: !!currentUser && canAccess('news_feed'),
        refetchInterval: 120_000,
        staleTime: 60_000
    });
    const newsUnreadCount = newsUnread?.unread || 0;

    // Category visibility checks — show group if user has access OR feature is locked-but-visible
    const isInternalUser = !currentUser?.clientMembership || currentUser?.admin;
    const showInSidebar = (key) => canAccess(key) || isLocked(key);
    // ProSpec360 is an external compartment available to everyone, so Tools is
    // always shown.
    const hasTools = true;
    const hasAnalytics = showInSidebar('profitability');
    const hasCommunication = showInSidebar('email') || showInSidebar('team_chat');

    // First available route per category (for collapsed icon click)
    const firstToolRoute = canAccess('ait') ? '/dashboard/ait' : (canAccess('audit_tracker') && isInternalUser) ? '/dashboard/audit-tracker' : canAccess('data_extraction') ? '/dashboard/data-extraction' : canAccess('studio') ? '/dashboard/imagen' : '/dashboard/prospec360';
    const firstAnalyticsRoute = '/dashboard/profitability-index';
    const firstCommunicationRoute = canAccess('email') ? '/dashboard/email' : '/dashboard/team-chat';

    const sidebarStyle = `md:translate-x-0 fixed inset-y-0 left-0 z-50 w-[80%] sm:w-72 ${collapsed ? 'md:w-16' : 'md:w-64'} transform transition-all duration-300 ease-in-out
        ${isOpen ? 'translate-x-0 shadow-lg' : '-translate-x-full'}
        ${theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}
        border-r ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}
        md:relative md:shadow-none md:h-screen overflow-y-auto overflow-x-hidden`;

    const linkBaseClass = `flex items-center ${collapsed ? 'justify-center px-2' : 'space-x-3 px-4'} py-3 rounded-lg transition-all duration-200 [&_svg]:shrink-0`;
    const linkActiveClass = `${theme === 'dark' ? 'bg-gray-700' : 'bg-blue-50 text-blue-600'}`;
    const linkInactiveClass = `hover:${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`;
    const childLinkClass = `flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-all duration-200 text-sm`;

    // Reusable category dropdown component
    const CategoryDropdown = ({ label, icon: Icon, expanded, setExpanded, firstRoute, badgeCount = 0, children }) => {
        if (collapsed) {
            return (
                <button
                    onClick={() => {
                        setCollapsed(false);
                        setExpanded(true);
                    }}
                    className={`${linkBaseClass} ${linkInactiveClass} relative`}
                    title={label}
                >
                    <Icon size={20} />
                    {badgeCount > 0 && (
                        <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-blue-600 rounded-full" />
                    )}
                </button>
            );
        }

        return (
            <div>
                <button
                    onClick={() => setExpanded(!expanded)}
                    className={`${linkBaseClass} ${linkInactiveClass} w-full justify-between`}
                >
                    <div className="flex items-center space-x-3">
                        <Icon size={20} />
                        <span>{label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {!expanded && badgeCount > 0 && (
                            <span className="bg-blue-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                                {badgeCount > 99 ? '99+' : badgeCount}
                            </span>
                        )}
                        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                </button>

                {expanded && (
                    <div className="ml-4 mt-1 space-y-0.5">
                        {children}
                    </div>
                )}
            </div>
        );
    };

    return (
        <aside className={sidebarStyle}>
            {/* Close button (mobile only) */}
            <button
                onClick={() => setIsOpen(false)}
                className="md:hidden absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
            >
                <X size={24} />
            </button>

            {/* Sidebar content */}
            <div className="p-4 h-full flex flex-col">
                {/* Header section with logo and theme toggle */}
                <div className="mb-6">
                    {/* Logo and theme toggle */}
                    <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'} mb-6 pr-8 md:pr-0`}>
                        <Link to="/dashboard" className="text-2xl font-dm-serif">
                            {collapsed ? <span className="hidden md:block text-lg">J</span> : 'JPRO'}
                            <span className="md:hidden">JPRO</span>
                        </Link>
                        {!collapsed && (
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={triggerScreensaver}
                                    className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                    aria-label="Start screensaver"
                                    title="Start screensaver (Ctrl/Cmd+Shift+S)"
                                >
                                    <MonitorPlay size={16} />
                                </button>
                                <button
                                    onClick={toggleTheme}
                                    className="p-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                    aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                                >
                                    {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
                                </button>
                            </div>
                        )}
                    </div>
                    {/* When collapsed on desktop, show theme + screensaver icons stacked under the logo */}
                    {collapsed && (
                        <div className="hidden md:flex flex-col items-center gap-1 mb-2">
                            <button
                                onClick={triggerScreensaver}
                                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300"
                                aria-label="Start screensaver"
                                title="Start screensaver (Ctrl/Cmd+Shift+S)"
                            >
                                <MonitorPlay size={18} />
                            </button>
                            <button
                                onClick={toggleTheme}
                                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300"
                                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                            >
                                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                            </button>
                        </div>
                    )}
                    {/* Desktop collapse toggle */}
                    <button
                        onClick={() => setCollapsed(!collapsed)}
                        className={`hidden md:flex items-center ${collapsed ? 'justify-center' : 'justify-between'} w-full ${collapsed ? 'px-2 py-3' : 'px-3 py-2'} rounded-lg transition-colors [&_svg]:shrink-0 ${
                            theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                        }`}
                        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {collapsed
                            ? <PanelLeftOpen size={24} />
                            : <><span className="text-xs">Collapse</span><PanelLeftClose size={16} /></>
                        }
                    </button>
                </div>

                {/* Navigation - flex-1 to take available space */}
                <nav className="space-y-1 font-rasa flex-1">
                    {/* ── General ── */}
                    <NavLink
                        to="/dashboard"
                        end
                        className={({ isActive }) =>
                            `${linkBaseClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                        }
                        title="Home"
                    >
                        <Home size={20} />
                        {!collapsed && <span>Home</span>}
                    </NavLink>

                    <NavLink
                        to="/dashboard/unified-chat"
                        onClick={() => {
                            selectProject(null);
                            localStorage.removeItem('unified_current_chat_id');
                            localStorage.removeItem('unified_current_session');
                            localStorage.removeItem('unified_rag_session_id');
                        }}
                        className={({ isActive }) =>
                            `${linkBaseClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                        }
                        title="Chat"
                    >
                        <MessageSquare size={20} />
                        {!collapsed && <span>Chat</span>}
                    </NavLink>

                    <NavLink
                        to="/dashboard/chats"
                        className={({ isActive }) =>
                            `${linkBaseClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                        }
                        title="Chat History"
                    >
                        <History size={20} />
                        {!collapsed && <span>Chat History</span>}
                    </NavLink>

                    {/* Projects Dropdown — icon-only link when collapsed */}
                    {!canAccess('projects') && isLocked('projects') && (
                        <SidebarFeatureItem
                            featureKey="projects"
                            route="/dashboard/projects"
                            className={({ isActive }) =>
                                `${linkBaseClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                            }
                        >
                            <Folder size={20} />
                            {!collapsed && <span>Projects</span>}
                        </SidebarFeatureItem>
                    )}
                    {canAccess('projects') && (collapsed ? (
                        <NavLink
                            to="/dashboard/projects"
                            className={({ isActive }) =>
                                `${linkBaseClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                            }
                            title="Projects"
                        >
                            <Folder size={20} />
                        </NavLink>
                    ) : (
                        <div>
                            <button
                                onClick={() => setProjectsExpanded(!projectsExpanded)}
                                className={`${linkBaseClass} ${linkInactiveClass} w-full justify-between`}
                            >
                                <div className="flex items-center space-x-3">
                                    <Folder size={20} />
                                    <span>Projects</span>
                                </div>
                                {projectsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>

                            {projectsExpanded && (
                                <div className="ml-4 mt-1 space-y-1">
                                    {projects
                                        .filter(p => !p.archived)
                                        .sort((a, b) => {
                                            if (a.pinned && !b.pinned) return -1;
                                            if (!a.pinned && b.pinned) return 1;
                                            return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
                                        })
                                        .slice(0, 5)
                                        .map(project => (
                                            <button
                                                key={project.id}
                                                onClick={() => {
                                                    navigate(`/dashboard/projects/${project.id}`);
                                                    setIsOpen(false);
                                                }}
                                                className={`flex items-center gap-2 px-4 py-2 rounded-lg w-full text-left transition-colors text-sm font-rasa ${
                                                    theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                                                }`}
                                            >
                                                <div
                                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                                    style={{ backgroundColor: project.color }}
                                                />
                                                {project.pinned && (
                                                    <Pin className="w-3 h-3 text-orange-500 fill-orange-500 flex-shrink-0" />
                                                )}
                                                <span className="truncate">{project.name}</span>
                                                {project.chats_count > 0 && (
                                                    <span className={`text-xs px-1.5 py-0.5 rounded-full ml-auto ${
                                                        theme === 'dark' ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                                                    }`}>
                                                        {project.chats_count}
                                                    </span>
                                                )}
                                            </button>
                                        ))}

                                    <NavLink
                                        to="/dashboard/projects"
                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-rasa transition-colors ${
                                            theme === 'dark'
                                                ? 'text-blue-400 hover:bg-gray-700'
                                                : 'text-blue-600 hover:bg-gray-100'
                                        }`}
                                        onClick={() => setIsOpen(false)}
                                    >
                                        <Plus size={16} />
                                        <span>View All Projects</span>
                                    </NavLink>
                                </div>
                            )}
                        </div>
                    ))}

                    <SidebarFeatureItem
                        featureKey="my_notes"
                        route="/dashboard/notes"
                        className={({ isActive }) =>
                            `${linkBaseClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                        }
                    >
                        <StickyNote size={20} />
                        {!collapsed && <span>My Notes</span>}
                    </SidebarFeatureItem>

                    <SidebarFeatureItem
                        featureKey="my_tasks"
                        route="/dashboard/tasks"
                        className={({ isActive }) =>
                            `${linkBaseClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                        }
                    >
                        <div className="relative">
                            <CheckSquare size={20} />
                            {collapsed && canAccess('my_tasks') && taskCount > 0 && (
                                <span className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 bg-blue-600 rounded-full" />
                            )}
                        </div>
                        {!collapsed && (
                            <span>
                                My Tasks
                                {canAccess('my_tasks') && taskCount > 0 && (
                                    <sup className="ml-0.5 text-[9px] font-bold text-blue-500">
                                        {taskCount > 99 ? '99+' : taskCount}
                                    </sup>
                                )}
                            </span>
                        )}
                    </SidebarFeatureItem>

                    <SidebarFeatureItem
                        featureKey="training_intelligence"
                        route="/dashboard/training"
                        className={({ isActive }) =>
                            `${linkBaseClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                        }
                    >
                        <GraduationCap size={20} />
                        {!collapsed && <span>Training</span>}
                    </SidebarFeatureItem>

                    <SidebarFeatureItem
                        featureKey="service_requests"
                        route="/dashboard/request-services"
                        className={({ isActive }) =>
                            `${linkBaseClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                        }
                        title="Request a Zeffert service"
                    >
                        <HandPlatter size={20} />
                        {!collapsed && <span>Request Services</span>}
                    </SidebarFeatureItem>

                    {(currentUser?.admin || ['director', 'manager', 'associate'].includes(currentUser?.role)) && (
                        <NavLink
                            to="/dashboard/service-requests"
                            className={({ isActive }) =>
                                `${linkBaseClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                            }
                            title="Service request inbox"
                        >
                            <Inbox size={20} />
                            {!collapsed && <span>SR Inbox</span>}
                        </NavLink>
                    )}

                    <NavLink
                        to="/dashboard/leaderboards"
                        className={({ isActive }) =>
                            `${linkBaseClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                        }
                        title="Leaderboards"
                    >
                        <Trophy size={20} />
                        {!collapsed && <span>Leaderboards</span>}
                    </NavLink>

                    <SidebarFeatureItem
                        featureKey="news_feed"
                        route="/dashboard/news"
                        className={({ isActive }) =>
                            `${linkBaseClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                        }
                    >
                        <div className="relative">
                            <Newspaper size={20} />
                            {collapsed && canAccess('news_feed') && newsUnreadCount > 0 && (
                                <span className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 bg-blue-600 rounded-full" />
                            )}
                        </div>
                        {!collapsed && (
                            <span>
                                Industry Briefs
                                {canAccess('news_feed') && newsUnreadCount > 0 && (
                                    <sup className="ml-0.5 text-[9px] font-bold text-blue-500">
                                        {newsUnreadCount > 99 ? '99+' : newsUnreadCount}
                                    </sup>
                                )}
                            </span>
                        )}
                    </SidebarFeatureItem>

                    {/* ── Tools ── */}
                    {hasTools && (
                        <CategoryDropdown
                            label="Tools"
                            icon={Wrench}
                            expanded={toolsExpanded}
                            setExpanded={setToolsExpanded}
                            firstRoute={firstToolRoute}
                        >
                            <SidebarFeatureItem
                                featureKey="ait"
                                route="/dashboard/ait"
                                className={({ isActive }) =>
                                    `${childLinkClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                                }
                                onClick={() => setIsOpen(false)}
                            >
                                <Calculator size={18} />
                                <span>AIT Lab</span>
                                <SidebarInfoTip
                                    title="Average Income Test"
                                    subtitle="Average Income Test Intelligence"
                                    description="Provides modeling and operational support for Average Income Test planning, scenario analysis, and strategic compliance decision-making."
                                />
                            </SidebarFeatureItem>
                            {/* audit_tracker keeps the isInternalUser parent gate so it never leaks to external users even if show_when_locked is on. */}
                            {isInternalUser && (
                                <SidebarFeatureItem
                                    featureKey="audit_tracker"
                                    route="/dashboard/audit-tracker"
                                    className={({ isActive }) =>
                                        `${childLinkClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                                    }
                                    onClick={() => setIsOpen(false)}
                                >
                                    <ClipboardCheck size={18} />
                                    <span>Audit Tracker</span>
                                </SidebarFeatureItem>
                            )}
                            <SidebarFeatureItem
                                featureKey="data_extraction"
                                route="/dashboard/data-extraction"
                                className={({ isActive }) =>
                                    `${childLinkClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                                }
                                onClick={() => setIsOpen(false)}
                            >
                                <Database size={18} />
                                <span>Doc Intelligence</span>
                                <SidebarInfoTip
                                    title="Document Intelligence"
                                    subtitle="Operational Intelligence"
                                    description="Transforms fragmented operational information into structured workflows and actionable insights."
                                />
                            </SidebarFeatureItem>
                            <SidebarFeatureItem
                                featureKey="studio"
                                route="/dashboard/imagen"
                                className={({ isActive }) =>
                                    `${childLinkClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                                }
                                onClick={() => setIsOpen(false)}
                            >
                                <Image size={18} />
                                <span>Studio</span>
                            </SidebarFeatureItem>
                            <SidebarFeatureItem
                                featureKey="studio"
                                route="/dashboard/documents"
                                className={({ isActive }) =>
                                    `${childLinkClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                                }
                                onClick={() => setIsOpen(false)}
                                title="Documents"
                            >
                                <FileText size={18} />
                                <span>Studio Archive</span>
                            </SidebarFeatureItem>
                            <SidebarFeatureItem
                                featureKey="training_studio"
                                route="/dashboard/training-studio"
                                className={({ isActive }) =>
                                    `${childLinkClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                                }
                                onClick={() => setIsOpen(false)}
                            >
                                <BookOpen size={18} />
                                <span>Training Studio</span>
                            </SidebarFeatureItem>
                            <NavLink
                                to="/dashboard/prospec360"
                                className={({ isActive }) =>
                                    `${childLinkClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                                }
                                onClick={() => setIsOpen(false)}
                            >
                                <HardHat size={18} />
                                <span>ProSpec360</span>
                                <SidebarInfoTip
                                    title="ProSpec360"
                                    subtitle="Construction Intelligence"
                                    description="Designed to support construction oversight, quality assurance, inspections, documentation, and project visibility."
                                />
                            </NavLink>
                        </CategoryDropdown>
                    )}

                    {/* ── Analytics ── */}
                    {hasAnalytics && (
                        <CategoryDropdown
                            label="Analytics"
                            icon={BarChart3}
                            expanded={analyticsExpanded}
                            setExpanded={setAnalyticsExpanded}
                            firstRoute={firstAnalyticsRoute}
                        >
                            <SidebarFeatureItem
                                featureKey="profitability"
                                route="/dashboard/profitability-index"
                                className={({ isActive }) =>
                                    `${childLinkClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                                }
                                onClick={() => setIsOpen(false)}
                            >
                                <BarChart3 size={18} />
                                <span>Profitability Index</span>
                                <SidebarInfoTip
                                    title="Profitability Index"
                                    subtitle="Intersection Analytics"
                                    description="Help you understand how multifamily decisions impact the profitability and affordability of various projects and initiatives."
                                />
                            </SidebarFeatureItem>
                        </CategoryDropdown>
                    )}

                    {/* ── Communication ── */}
                    {hasCommunication && (
                        <CategoryDropdown
                            label="Communication"
                            icon={MessageCircle}
                            expanded={communicationExpanded}
                            setExpanded={setCommunicationExpanded}
                            firstRoute={firstCommunicationRoute}
                            badgeCount={teamChatUnreadCount}
                        >
                            <SidebarFeatureItem
                                featureKey="email"
                                route="/dashboard/email"
                                className={({ isActive }) =>
                                    `${childLinkClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                                }
                                onClick={() => setIsOpen(false)}
                            >
                                <Mail size={18} />
                                <span>Email</span>
                            </SidebarFeatureItem>
                            <SidebarFeatureItem
                                featureKey="team_chat"
                                route="/dashboard/team-chat"
                                className={({ isActive }) =>
                                    `${childLinkClass} ${isActive ? linkActiveClass : linkInactiveClass} justify-between`
                                }
                                onClick={() => setIsOpen(false)}
                            >
                                <div className="flex items-center space-x-3">
                                    <Users size={18} />
                                    <span>Teamspace</span>
                                </div>
                                {canAccess('team_chat') && teamChatUnreadCount > 0 && (
                                    <span className="bg-blue-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                                        {teamChatUnreadCount > 99 ? '99+' : teamChatUnreadCount}
                                    </span>
                                )}
                            </SidebarFeatureItem>
                        </CategoryDropdown>
                    )}

                    {/* ── Management (Org Hierarchy) ── */}
                    {(currentUser?.role === 'director' || currentUser?.role === 'manager' || currentUser?.admin) && (
                        <NavLink
                            to="/dashboard/management"
                            className={({ isActive }) =>
                                `${linkBaseClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                            }
                            title="Management"
                        >
                            <GitBranch size={20} />
                            {!collapsed && <span>Management</span>}
                        </NavLink>
                    )}

                    {/* ── Client Management ── */}
                    {currentUser?.isClientManager && (
                        <NavLink
                            to="/dashboard/client-management"
                            className={({ isActive }) =>
                                `${linkBaseClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                            }
                            title="Client Management"
                        >
                            <Users size={20} />
                            {!collapsed && <span>Client Management</span>}
                        </NavLink>
                    )}

                    {/* ── Client Audit Tracker (for external client users) ── */}
                    {currentUser?.clientMembership && canAccess('audit_tracker') && !currentUser?.admin && (
                        <NavLink
                            to="/dashboard/client-audit-tracker"
                            className={({ isActive }) =>
                                `${linkBaseClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                            }
                            onClick={() => setIsOpen(false)}
                            title="Audit Tracker"
                        >
                            <ClipboardCheck size={20} />
                            {!collapsed && <span>Audit Tracker</span>}
                        </NavLink>
                    )}

                    {/* ── Admin ── */}
                    {currentUser?.admin && (
                        <NavLink
                            to="/dashboard/admin"
                            className={({ isActive }) =>
                                `${linkBaseClass} ${isActive ? linkActiveClass : linkInactiveClass}`
                            }
                            title="Admin"
                        >
                            <LayoutDashboard size={20} />
                            {!collapsed && <span>Admin</span>}
                        </NavLink>
                    )}
                </nav>

                {/* Bottom section - appearance controls + user profile dropdown */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                    {/* Collapsed appearance controls: stack above the user avatar */}
                    {collapsed && (
                        <div className="hidden md:flex flex-col items-center gap-1 mb-2">
                            <button
                                onClick={triggerScreensaver}
                                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300"
                                aria-label="Start screensaver"
                                title="Start screensaver (Ctrl/Cmd+Shift+S)"
                            >
                                <MonitorPlay size={18} />
                            </button>
                            <button
                                onClick={toggleTheme}
                                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300"
                                aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                            >
                                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                            </button>
                        </div>
                    )}
                    <div className="relative w-full" ref={dropdownRef}>
                        <button
                            className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'} w-full px-2 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none`}
                            onClick={() => setDropdownOpen(!dropdownOpen)}
                        >
                            <div className="flex items-center space-x-3">
                                {currentUser?.avatarUrl ? (
                                    <img
                                        src={currentUser.avatarUrl}
                                        alt={`${currentUser.firstName || currentUser.first_name} ${currentUser.lastName || currentUser.last_name}`}
                                        className="w-8 h-8 rounded-full flex-shrink-0"
                                    />
                                ) : (
                                    <div className="w-8 h-8 rounded-full bg-blue-500 font-unna flex items-center justify-center text-white flex-shrink-0">
                                        {(currentUser?.firstName || currentUser?.first_name)?.charAt(0) || <User size={18} />}
                                    </div>
                                )}
                                {!collapsed && (
                                    <div className="flex-1 min-w-0 text-left">
                                        <p className="text-sm font-medium font-unna truncate">
                                            {currentUser?.firstName || currentUser?.first_name} {currentUser?.lastName || currentUser?.last_name}
                                        </p>
                                    </div>
                                )}
                            </div>
                            {!collapsed && (dropdownOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                        </button>

                        {dropdownOpen && (
                            <div className={`absolute ${collapsed ? 'left-full ml-2' : 'left-0'} bottom-full mb-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg overflow-hidden z-50 transition-all duration-150`}>
                                <div className="py-2">
                                    <div className="px-4 py-2 font-medium font-unna border-b border-gray-200 dark:border-gray-700 text-sm truncate">
                                        {currentUser?.email}
                                    </div>
                                    <button
                                        onClick={() => {
                                            setDropdownOpen(false);
                                            onOpenDrawer && onOpenDrawer();
                                        }}
                                        className="flex items-center justify-between w-full px-4 py-2 font-unna text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        <span className="flex items-center gap-2">

                                            Announcements
                                            <Bell size={16} />
                                        </span>
                                        {unreadCount > 0 && (
                                            <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                                                {unreadCount}
                                            </span>
                                        )}
                                    </button>
                                    {/* <Link
                                        to="/dashboard/upgrade"
                                        className="flex items-center gap-2 px-4 py-2 font-unna text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        onClick={() => setDropdownOpen(false)}
                                    >
                                        Upgrade
                                        <Zap size={14} />
                                    </Link> */}
                                    <Link
                                        to="/dashboard/settings"
                                        className="block px-4 py-2 font-unna text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        onClick={() => setDropdownOpen(false)}
                                    >
                                        Settings
                                    </Link>
                                    <button
                                        onClick={() => {
                                            setDropdownOpen(false);
                                            logout();
                                        }}
                                        className="block w-full text-left px-4 font-unna py-2 text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    >
                                        Sign out
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

        </aside>
    );
}

export default DashboardSidebar;
